import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { API, resolveImg } from "@/App";
import SafeImg from "@/components/SafeImg";
import Seo from "@/components/Seo";

export default function Members() {
  const [members, setMembers] = useState([]);
  const [meta, setMeta] = useState({ years: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeYear, setActiveYear] = useState("all");
  // Default: show only active members (with photo). Unchecking reveals historical members too.
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/members`),
      axios.get(`${API}/members/meta`),
    ]).then(([m, meta]) => {
      setMembers(m.data);
      setMeta(meta.data);
      setLoading(false);
    });
  }, []);

  const activeMembers = useMemo(() => {
    let list = members.filter((m) => m.active);
    if (activeCategory !== "all") list = list.filter((m) => m.category === activeCategory);
    if (activeYear !== "all") list = list.filter((m) => String(m.year_joined) === activeYear);
    return list;
  }, [members, activeCategory, activeYear]);

  const inactiveMembers = useMemo(() => {
    if (!showAll) return [];
    let list = members.filter((m) => !m.active);
    if (activeCategory !== "all") list = list.filter((m) => m.category === activeCategory);
    if (activeYear !== "all") list = list.filter((m) => String(m.year_joined) === activeYear);
    return list;
  }, [members, activeCategory, activeYear, showAll]);

  // Group active members by year
  const activeGrouped = useMemo(() => {
    const map = new Map();
    activeMembers.forEach((m) => {
      const y = m.year_joined || 0;
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(m);
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [activeMembers]);

  // Group inactive by year (historical)
  const inactiveGrouped = useMemo(() => {
    const map = new Map();
    inactiveMembers.forEach((m) => {
      const y = m.year_joined || 0;
      if (!map.has(y)) map.set(y, []);
      map.get(y).push(m);
    });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [inactiveMembers]);

  return (
    <div data-testid="members-page">
      <Seo
        title="Miembros"
        description="Directorio de miembros de la Tertulia In Dubio (Sevilla): activos e históricos, organizados por categoría profesional (Derecho, Ciencia y Tecnología, Economía, Humanidades, General) y año de incorporación desde 2014."
        path="/miembros"
      />
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-10 md:pt-16 pb-10">
        <span className="eyebrow"><span className="decor-line"></span>Quiénes somos</span>
        <div className="grid lg:grid-cols-[1.3fr_0.7fr] gap-10 items-end mt-4">
          <h1 className="font-serif text-[11vw] md:text-[96px] lg:text-[120px] leading-[0.88] tracking-tight font-light">
            Miembros.
          </h1>
          <p className="prose-bio max-w-md">
            Los miembros de la Tertulia In Dubio provienen de una amplia gama de disciplinas y profesiones. Esta <strong>pluralidad</strong> es fundamental para abordar cada tema desde múltiples ángulos y fomentar un debate inclusivo y profundo.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="border-y border-[var(--line)] py-5 flex flex-wrap items-center gap-4">
          <div className="flex flex-wrap gap-1 items-center" data-testid="category-filters">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase border-b ${activeCategory === "all" ? "border-[var(--text-ink)] text-[var(--text-ink)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-ink)]"}`}
              data-testid="cat-all"
            >Todas <span className="text-[var(--gold-deep)] ml-1">{members.filter((m)=>showAll || m.active).length}</span></button>
            {meta.categories.map((c) => {
              const count = members.filter((m) => m.category === c && (showAll || m.active)).length;
              if (count === 0) return null;
              return (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  className={`px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase border-b ${activeCategory === c ? "border-[var(--text-ink)] text-[var(--text-ink)]" : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-ink)]"}`}
                  data-testid={`cat-${c.toLowerCase().replace(/\s+/g,'-')}`}
                >{c} <span className="text-[var(--gold-deep)] ml-1">{count}</span></button>
              );
            })}
          </div>

          <div className="ml-auto flex items-center gap-4">
            <select
              value={activeYear}
              onChange={(e) => setActiveYear(e.target.value)}
              className="bg-transparent border-b border-[var(--line)] py-1 px-2 text-[12px] uppercase tracking-[0.18em] focus:outline-none focus:border-[var(--gold)]"
              data-testid="year-filter"
            >
              <option value="all">Todos los años</option>
              {meta.years.map((y) => <option key={y} value={y}>Desde {y}</option>)}
            </select>
            <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[var(--text-muted)] cursor-pointer" data-testid="show-all-toggle">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Incluir histórico
            </label>
          </div>
        </div>
      </section>

      {/* Active grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-10 md:py-14">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-[rgba(18,18,18,0.05)] animate-pulse"></div>
            ))}
          </div>
        ) : activeMembers.length === 0 && inactiveMembers.length === 0 ? (
          <div className="text-center py-20 text-[var(--text-muted)]">
            <p className="font-serif text-3xl">Ningún miembro coincide con estos filtros.</p>
          </div>
        ) : (
          <div className="space-y-16" data-testid="members-grid">
            {activeGrouped.map(([year, items]) => (
              <div key={`active-${year}`}>
                <div className="flex items-baseline gap-6 mb-6">
                  <h2 className="font-serif text-5xl md:text-6xl leading-none text-[var(--text-ink)]">
                    Desde {year || "—"}
                  </h2>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                    {items.length} {items.length === 1 ? "miembro activo" : "miembros activos"}
                  </span>
                  <div className="flex-1 h-px bg-[var(--line)]"></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-7 gap-y-12">
                  {items.map((m, i) => (
                    <article
                      key={m.id}
                      className="anim-fadeup"
                      style={{ animationDelay: `${Math.min(i * 0.02, 0.3)}s` }}
                      data-testid={`member-${m.slug}`}
                    >
                      <div className="aspect-[3/4] overflow-hidden border border-[rgba(255,255,255,0.7)] shadow-[0_14px_40px_-20px_rgba(22,40,60,0.2)] group relative">
                        {m.image ? (
                          <SafeImg
                            src={m.image}
                            alt={m.name}
                            name={m.name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                            style={{ filter: "saturate(0.9)" }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-[rgba(18,18,18,0.04)] font-serif text-5xl text-[var(--text-muted)]">
                            {m.name.split(" ").map((n)=>n[0]).slice(0,2).join("")}
                          </div>
                        )}
                      </div>
                      <h3 className="font-serif text-[22px] mt-4 leading-tight">{m.name}</h3>
                      <span className="text-[9.5px] uppercase tracking-[0.22em] text-[var(--gold-deep)] mt-1.5 block">
                        {m.category}
                      </span>
                      <p className="text-[12px] text-[var(--text-muted)] italic mt-1.5 font-light leading-snug">
                        {m.role}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
            ))}

            {/* Historical section - only shown if showAll or there are inactive members matching filters */}
            {inactiveGrouped.length > 0 && (
              <div className="pt-10 border-t border-[var(--line)] mt-16" data-testid="historical-section">
                <div className="mb-10 max-w-2xl">
                  <span className="eyebrow">Archivo histórico</span>
                  <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-tight text-[var(--text-muted)]">
                    Quienes formaron parte.
                  </h2>
                  <p className="text-[13px] text-[var(--text-muted)] mt-3 max-w-xl leading-relaxed">
                    Miembros históricos que pertenecieron a la tertulia y que hoy figuran en su archivo como testimonio de una década de pluralidad.
                  </p>
                </div>
                <div className="space-y-10">
                  {inactiveGrouped.map(([year, items]) => (
                    <div key={`inactive-${year}`}>
                      <div className="flex items-baseline gap-4 mb-4">
                        <h3 className="font-serif text-3xl text-[var(--text-muted)]">{year}</h3>
                        <span className="text-[10.5px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                          {items.length} {items.length === 1 ? "miembro" : "miembros"}
                        </span>
                        <div className="flex-1 h-px bg-[var(--line)]"></div>
                      </div>
                      <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                        {items.map((m) => (
                          <li key={m.id} className="flex items-baseline gap-4 border-b border-dashed border-[var(--line)] pb-3" data-testid={`historical-${m.slug}`}>
                            <span className="font-serif text-[19px] leading-tight text-[var(--text-ink)] flex-shrink-0">{m.name}</span>
                            <span className="text-[11.5px] text-[var(--text-muted)] italic leading-snug text-right ml-auto">
                              {m.role}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
